import { definePreset } from '@openng/optimus-ui-themes'
import Aura from '@openng/optimus-ui-themes/aura'

/**
 * Baboor brand palettes.
 *
 * Primary    — Vibrant green #23B21B (matching brand logo / main action color)
 * Secondary  — Muted green   #8BA888 (success / secondary actions)
 * Tertiary   — Warm brown    #A68966 (accent elements)
 */
export const baboorPrimaryPalette = {
    50: '#FDF8F7',
    100: '#F9ECE9',
    200: '#F4D5D0',
    300: '#E8AFAA',
    400: '#D47A70',
    500: '#B94C41',
    600: '#B23A2E',
    700: '#962E24',
    800: '#7B271F',
    900: '#67251E',
    950: '#38100C',
} as const

export const baboorSecondaryPalette = {
    50: '#F2F6F2',
    100: '#E0E9E1',
    200: '#C2D3C5',
    300: '#A5BCA8',
    400: '#98B29B',
    500: '#8BA888',
    600: '#71906F',
    700: '#5C765B',
    800: '#4C604C',
    900: '#3E4F3E',
    950: '#273027',
} as const

export const baboorTertiaryPalette = {
    50: '#F7F4EF',
    100: '#ECE4D7',
    200: '#D9CAB1',
    300: '#C6AF8A',
    400: '#B69D75',
    500: '#A68966',
    600: '#8D7354',
    700: '#715D45',
    800: '#5B4B38',
    900: '#4A3D2F',
    950: '#292118',
} as const

export const baboorDangerPalette = {
    50: '#FBF2F2',
    100: '#F5DEDD',
    200: '#E9BDBB',
    300: '#DB9B99',
    400: '#CF7976',
    500: '#C4554F',
    600: '#A44540',
    700: '#853632',
    800: '#692B28',
    900: '#532321',
    950: '#2E1413',
} as const

/**
 * Custom Optimus UI / PrimeNG preset for the Baboor brand.
 * Derives from the Aura preset and rebrands the primary palette
 * plus radius scale so every `p-*` component follows the design system.
 */
export const baboorLightSurfacePalette = {
    0: '#FFFFFF',
    50: '#FDFBF6', // background-2: warm alabaster surface, cards, panels
    100: '#F9F6EF', // hover state
    200: '#F3EEE3', // background-1: main warm page background
    300: '#F1EADB', // background-3: muted containers & chips
    400: '#E8E2D5', // border color
    500: '#D5CBBF', // border strong
    600: '#8A8275', // muted text / subtle icons
    700: '#4A443B', // secondary text
    800: '#2B261F', // strong text
    900: '#17150F', // background-4 / text-primary
    950: '#0D0C08', // deep charcoal
} as const

export const baboorDarkSurfacePalette = {
    0: '#FFFFFF',
    50: '#FDFBF6', // text-primary in dark mode
    100: '#F4EFE6', // text-secondary in dark mode
    200: '#DDD5C7',
    300: '#B8ADA0',
    400: '#8A8072', // muted text in dark mode
    500: '#635B50',
    600: '#4F493D', // dark border strong
    700: '#3A352B', // dark border
    800: '#2A261E', // background-3: dark muted container
    900: '#1E1B15', // background-2: dark surface, cards, panels, dialogs
    950: '#17150F', // background-1 / background-4: dark page background
} as const

/**
 * Custom Optimus UI preset for the Hair Style Club brand.
 * Derives from the Aura preset and unifies the primary palette,
 * warm ivory/charcoal surface palettes, radius scale, and light/dark
 * semantic tokens so every `p-*` component adheres to the design system.
 */
export const BaboorPreset = definePreset(Aura, {
    primitive: {
        borderRadius: {
            none: '0',
            xs: '2px',
            sm: '6px',
            md: '8px',
            lg: '10px',
            xl: '14px',
        },
        green: baboorPrimaryPalette,
        emerald: baboorPrimaryPalette,
    },
    semantic: {
        primary: baboorPrimaryPalette,
        colorScheme: {
            light: {
                surface: baboorLightSurfacePalette,
                primary: {
                    color: '{primary.500}',
                    contrastColor: '#FFFFFF',
                    hoverColor: '{primary.600}',
                    activeColor: '{primary.700}',
                },
                highlight: {
                    background: '{primary.50}',
                    focusBackground: '{primary.100}',
                    color: '{primary.700}',
                    focusColor: '{primary.800}',
                },
                formField: {
                    background: '{surface.50}',
                    disabledBackground: '{surface.200}',
                    filledBackground: '{surface.100}',
                    filledHoverBackground: '{surface.100}',
                    filledFocusBackground: '{surface.50}',
                    borderColor: '{surface.400}',
                    hoverBorderColor: '{surface.500}',
                    focusBorderColor: '{primary.color}',
                    invalidBorderColor: '#C4554F',
                    color: '{surface.900}',
                    disabledColor: '{surface.600}',
                    placeholderColor: '{surface.600}',
                },
                text: {
                    color: '{surface.900}',
                    hoverColor: '{surface.950}',
                    mutedColor: '{surface.600}',
                    hoverMutedColor: '{surface.700}',
                },
                content: {
                    background: '{surface.50}',
                    hoverBackground: '{surface.100}',
                    borderColor: '{surface.400}',
                    color: '{text.color}',
                },
                overlay: {
                    select: {
                        background: '{surface.50}',
                        borderColor: '{surface.400}',
                        color: '{text.color}',
                    },
                    popover: {
                        background: '{surface.50}',
                        borderColor: '{surface.400}',
                        color: '{text.color}',
                    },
                    modal: {
                        background: '{surface.50}',
                        borderColor: '{surface.400}',
                        color: '{text.color}',
                    },
                },
            },
            dark: {
                surface: baboorDarkSurfacePalette,
                primary: {
                    color: '{primary.400}',
                    contrastColor: '{surface.950}',
                    hoverColor: '{primary.300}',
                    activeColor: '{primary.200}',
                },
                highlight: {
                    background:
                        'color-mix(in srgb, {primary.500}, transparent 84%)',
                    focusBackground:
                        'color-mix(in srgb, {primary.500}, transparent 76%)',
                    color: '{surface.50}',
                    focusColor: '{surface.0}',
                },
                formField: {
                    background: '{surface.900}',
                    disabledBackground: '{surface.800}',
                    filledBackground: '{surface.800}',
                    filledHoverBackground: '{surface.800}',
                    filledFocusBackground: '{surface.900}',
                    borderColor: '{surface.700}',
                    hoverBorderColor: '{surface.600}',
                    focusBorderColor: '{primary.color}',
                    invalidBorderColor: '#DB9B99',
                    color: '{surface.50}',
                    disabledColor: '{surface.400}',
                    placeholderColor: '{surface.400}',
                },
                text: {
                    color: '{surface.50}',
                    hoverColor: '{surface.0}',
                    mutedColor: '{surface.400}',
                    hoverMutedColor: '{surface.300}',
                },
                content: {
                    background: '{surface.900}',
                    hoverBackground: '{surface.800}',
                    borderColor: '{surface.700}',
                    color: '{text.color}',
                },
                overlay: {
                    select: {
                        background: '{surface.900}',
                        borderColor: '{surface.700}',
                        color: '{text.color}',
                    },
                    popover: {
                        background: '{surface.900}',
                        borderColor: '{surface.700}',
                        color: '{text.color}',
                    },
                    modal: {
                        background: '{surface.900}',
                        borderColor: '{surface.700}',
                        color: '{text.color}',
                    },
                },
            },
        },
    },
    components: {
        button: {
            colorScheme: {
                light: {
                    root: {
                        primary: {
                            background: '{primary.color}',
                            hoverBackground: '{primary.hover.color}',
                            activeBackground: '{primary.active.color}',
                            borderColor: '{primary.color}',
                            hoverBorderColor: '{primary.hover.color}',
                            activeBorderColor: '{primary.active.color}',
                            color: '{primary.contrast.color}',
                            hoverColor: '{primary.contrast.color}',
                            activeColor: '{primary.contrast.color}',
                            focusRing: {
                                color: '{primary.color}',
                                shadow: 'none',
                            },
                        },
                        success: {
                            background: '{primary.color}',
                            hoverBackground: '{primary.hover.color}',
                            activeBackground: '{primary.active.color}',
                            borderColor: '{primary.color}',
                            hoverBorderColor: '{primary.hover.color}',
                            activeBorderColor: '{primary.active.color}',
                            color: '{primary.contrast.color}',
                            hoverColor: '{primary.contrast.color}',
                            activeColor: '{primary.contrast.color}',
                            focusRing: {
                                color: '{primary.color}',
                                shadow: 'none',
                            },
                        },
                    },
                    outlined: {
                        primary: {
                            hoverBackground: '{primary.50}',
                            activeBackground: '{primary.100}',
                            borderColor: '{primary.500}',
                            color: '{primary.color}',
                        },
                        success: {
                            hoverBackground: '{primary.50}',
                            activeBackground: '{primary.100}',
                            borderColor: '{primary.500}',
                            color: '{primary.color}',
                        },
                    },
                    text: {
                        primary: {
                            hoverBackground: '{primary.50}',
                            activeBackground: '{primary.100}',
                            color: '{primary.color}',
                        },
                        success: {
                            hoverBackground: '{primary.50}',
                            activeBackground: '{primary.100}',
                            color: '{primary.color}',
                        },
                    },
                    link: {
                        color: '{primary.color}',
                        hoverColor: '{primary.hover.color}',
                        activeColor: '{primary.active.color}',
                    },
                },
                dark: {
                    root: {
                        primary: {
                            background: '{primary.color}',
                            hoverBackground: '{primary.hover.color}',
                            activeBackground: '{primary.active.color}',
                            borderColor: '{primary.color}',
                            hoverBorderColor: '{primary.hover.color}',
                            activeBorderColor: '{primary.active.color}',
                            color: '{primary.contrast.color}',
                            hoverColor: '{primary.contrast.color}',
                            activeColor: '{primary.contrast.color}',
                            focusRing: {
                                color: '{primary.color}',
                                shadow: 'none',
                            },
                        },
                        success: {
                            background: '{primary.color}',
                            hoverBackground: '{primary.hover.color}',
                            activeBackground: '{primary.active.color}',
                            borderColor: '{primary.color}',
                            hoverBorderColor: '{primary.hover.color}',
                            activeBorderColor: '{primary.active.color}',
                            color: '{primary.contrast.color}',
                            hoverColor: '{primary.contrast.color}',
                            activeColor: '{primary.contrast.color}',
                            focusRing: {
                                color: '{primary.color}',
                                shadow: 'none',
                            },
                        },
                    },
                    outlined: {
                        primary: {
                            hoverBackground:
                                'color-mix(in srgb, {primary.color}, transparent 96%)',
                            activeBackground:
                                'color-mix(in srgb, {primary.color}, transparent 84%)',
                            borderColor: '{primary.400}',
                            color: '{primary.color}',
                        },
                        success: {
                            hoverBackground:
                                'color-mix(in srgb, {primary.color}, transparent 96%)',
                            activeBackground:
                                'color-mix(in srgb, {primary.color}, transparent 84%)',
                            borderColor: '{primary.400}',
                            color: '{primary.color}',
                        },
                    },
                    text: {
                        primary: {
                            hoverBackground:
                                'color-mix(in srgb, {primary.color}, transparent 96%)',
                            activeBackground:
                                'color-mix(in srgb, {primary.color}, transparent 84%)',
                            color: '{primary.color}',
                        },
                        success: {
                            hoverBackground:
                                'color-mix(in srgb, {primary.color}, transparent 96%)',
                            activeBackground:
                                'color-mix(in srgb, {primary.color}, transparent 84%)',
                            color: '{primary.color}',
                        },
                    },
                    link: {
                        color: '{primary.color}',
                        hoverColor: '{primary.hover.color}',
                        activeColor: '{primary.active.color}',
                    },
                },
            },
        },
    },
})
